import { supabase } from '../lib/supabase';
import { format, subMonths, startOfMonth } from 'date-fns';

/**
 * Format currency to Turkish Lira
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })?.format(amount);
};

/**
 * Format large numbers to K/M notation
 */
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000)?.toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000)?.toFixed(1) + 'K';
  }
  return num?.toString();
};

/**
 * Fetch comprehensive analytics data for dashboard
 */
export const fetchAnalyticsData = async (userId, timeRange = '6months') => {
  try {
    // Determine date range
    const monthsBack = timeRange === '3months' ? 3 : timeRange === '12months' ? 12 : 6;
    const startDate = startOfMonth(subMonths(new Date(), monthsBack));

    // Fetch from public.campaign_links
    const { data: campaignLinks, error: campaignsError } = await supabase
      ?.from('campaign_links')
      ?.select(`
        *,
        link_clicks(
          id,
          platform,
          country,
          device,
          clicked_at,
          influencer_id
        )
      `)
      ?.eq('user_id', userId)
      ?.eq('is_active', true)
      ?.gte('created_at', startDate?.toISOString());

    if (campaignsError) throw campaignsError;

    // Normalize public.campaign_links fields to the shape expected by downstream functions
    const campaigns = (campaignLinks || [])?.map(link => ({
      ...link,
      campaign_id: link?.campaign_id || null,
      campaign_slug: link?.campaign_slug || link?.slug || '',
      youtube_music_url: link?.youtube_music_url || '',
      is_active: link?.is_active ?? true,
      // Downstream analytics shape
      artist_id: link?.user_id,
      budget: 0,
      status: link?.is_active ? 'active' : 'completed',
      campaign_influencers: link?.influencer_id
        ? [{
            id: link?.id,
            influencer_id: link?.influencer_id,
            allocated_budget: 0,
            proposal_accepted: true,
            influencers: { follower_count: 0, engagement_rate: 0, platforms: [] }
          }]
        : []
    }));

    return {
      campaigns,
      startDate,
      endDate: new Date()
    };
  } catch (error) {
    console.error('Error fetching artist campaigns:', error);
    return { campaigns: [], startDate: new Date(), endDate: new Date() };
  }
};

/**
 * Calculate KPI metrics from campaign data
 */
export const calculateKPIMetrics = (campaigns) => {
  const totalCampaigns = campaigns?.length || 0;
  const activeCampaigns = campaigns?.filter(c => c?.status === 'active')?.length || 0;
  const completedCampaigns = campaigns?.filter(c => c?.status === 'completed')?.length || 0;

  // Calculate total budget and reach
  let totalBudget = 0;
  let totalReach = 0;
  let totalEngagementSum = 0;
  let engagementCount = 0;

  campaigns?.forEach(campaign => {
    totalBudget += parseFloat(campaign?.budget || 0);

    campaign?.campaign_influencers?.forEach(ci => {
      if (ci?.proposal_accepted) {
        totalReach += ci?.influencers?.follower_count || 0;
        const engRate = parseFloat(ci?.influencers?.engagement_rate || 0);
        if (engRate > 0) {
          totalEngagementSum += engRate;
          engagementCount++;
        }
      }
    });
  });

  const avgEngagement = engagementCount > 0 ? totalEngagementSum / engagementCount : 0;

  // Calculate ROI (simplified: (reach * engagement) / budget)
  const effectiveReach = totalReach * (avgEngagement / 100);
  const roi = totalBudget > 0 ? ((effectiveReach / totalBudget) * 100)?.toFixed(1) : '0.0';

  // Find top performing campaign
  let topCampaign = null;
  let maxPerformance = 0;

  campaigns?.forEach(campaign => {
    let campaignReach = 0;
    let campaignEngagement = 0;
    let count = 0;

    campaign?.campaign_influencers?.forEach(ci => {
      if (ci?.proposal_accepted) {
        campaignReach += ci?.influencers?.follower_count || 0;
        campaignEngagement += parseFloat(ci?.influencers?.engagement_rate || 0);
        count++;
      }
    });

    const avgCampaignEng = count > 0 ? campaignEngagement / count : 0;
    const performance = campaignReach * (avgCampaignEng / 100);

    if (performance > maxPerformance) {
      maxPerformance = performance;
      topCampaign = campaign?.name;
    }
  });

  return {
    totalCampaigns: totalCampaigns?.toString(),
    avgROI: `${roi}%`,
    topPerforming: topCampaign || 'N/A',
    growthRate: '+18.5%', // Simulated growth
    totalReach: formatNumber(totalReach),
    avgEngagement: avgEngagement?.toFixed(1),
    totalBudget: formatCurrency(totalBudget),
    activeCampaigns,
    completedCampaigns
  };
};

/**
 * Generate campaign performance chart data
 */
export const generateCampaignPerformanceData = (campaigns) => {
  return campaigns?.slice(0, 8)?.map(campaign => {
    let reach = 0;
    let engagement = 0;
    let conversions = 0;
    let count = 0;

    campaign?.campaign_influencers?.forEach(ci => {
      if (ci?.proposal_accepted) {
        reach += ci?.influencers?.follower_count || 0;
        engagement += parseFloat(ci?.influencers?.engagement_rate || 0) * 1000; // Scale for visibility
        count++;
      }
    });

    // Simulate conversions based on engagement
    conversions = Math.floor(reach * 0.02); // 2% conversion rate

    return {
      name: campaign?.name?.substring(0, 15) || 'Campaign',
      reach: Math.floor(reach / 1000), // Convert to thousands
      engagement: Math.floor(engagement / count) || 0,
      conversions
    };
  }) || [];
};

/**
 * Generate earnings trends data by month
 */
export const generateEarningsTrendsData = (campaigns, monthsBack = 6) => {
  const monthlyData = {};

  // Initialize months
  for (let i = monthsBack - 1; i >= 0; i--) {
    const date = subMonths(new Date(), i);
    const monthKey = format(date, 'MMM yyyy');
    monthlyData[monthKey] = {
      month: format(date, 'MMM'),
      earnings: 0,
      commission: 0,
      net: 0
    };
  }

  // Aggregate earnings by month
  campaigns?.forEach(campaign => {
    const campaignDate = new Date(campaign?.created_at);
    const monthKey = format(campaignDate, 'MMM yyyy');

    if (monthlyData?.[monthKey]) {
      const budget = parseFloat(campaign?.budget || 0);
      const commission = budget * 0.15;
      const net = budget - commission;

      monthlyData[monthKey].earnings += budget;
      monthlyData[monthKey].commission += commission;
      monthlyData[monthKey].net += net;
    }
  });

  return Object.values(monthlyData)?.map(data => ({
    month: data?.month,
    earnings: Math.round(data?.earnings),
    commission: Math.round(data?.commission),
    net: Math.round(data?.net)
  }));
};

/**
 * Generate ROI comparison data
 */
export const generateROIComparisonData = (campaigns) => {
  const statusGroups = {
    active: { label: 'Active', totalBudget: 0, totalReach: 0, totalEngagement: 0, count: 0 },
    completed: { label: 'Completed', totalBudget: 0, totalReach: 0, totalEngagement: 0, count: 0 },
    pending: { label: 'Pending', totalBudget: 0, totalReach: 0, totalEngagement: 0, count: 0 }
  };

  campaigns?.forEach(campaign => {
    const status = campaign?.status;
    if (!statusGroups?.[status]) return;

    statusGroups[status].totalBudget += parseFloat(campaign?.budget || 0);

    campaign?.campaign_influencers?.forEach(ci => {
      if (ci?.proposal_accepted) {
        statusGroups[status].totalReach += ci?.influencers?.follower_count || 0;
        statusGroups[status].totalEngagement += parseFloat(ci?.influencers?.engagement_rate || 0);
        statusGroups[status].count++;
      }
    });
  });

  return Object.values(statusGroups)?.map(group => {
    const avgEngagement = group?.count > 0 ? group?.totalEngagement / group?.count : 0;
    const effectiveReach = group?.totalReach * (avgEngagement / 100);
    const roi = group?.totalBudget > 0 ? ((effectiveReach / group?.totalBudget) * 100)?.toFixed(1) : 0;

    return {
      name: group?.label,
      roi: parseFloat(roi),
      budget: Math.round(group?.totalBudget),
      reach: Math.floor(group?.totalReach / 1000) // In thousands
    };
  });
};

/**
 * Generate platform distribution data
 */
export const generatePlatformDistributionData = (campaigns) => {
  const platformCounts = {};

  campaigns?.forEach(campaign => {
    campaign?.campaign_influencers?.forEach(ci => {
      if (ci?.proposal_accepted && ci?.influencers?.platforms) {
        ci?.influencers?.platforms?.forEach(platform => {
          const platformName = platform?.name || 'Other';
          platformCounts[platformName] = (platformCounts?.[platformName] || 0) + 1;
        });
      }
    });
  });

  return Object.entries(platformCounts)?.map(([name, value]) => ({
    name,
    value
  }));
};

/**
 * Fetch real click analytics from link_clicks table
 * Returns: total clicks, per platform, per influencer, per campaign
 */
export const fetchLinkClicksAnalytics = async (userId) => {
  try {
    // Get all campaign links owned by this user
    const { data: links, error: linksError } = await supabase?.from('campaign_links')?.select('id, title, campaign_id, influencer_id')?.eq('user_id', userId);

    if (linksError) throw linksError;

    if (!links || links?.length === 0) {
      return {
        data: {
          totalClicks: 0,
          byPlatform: [],
          byInfluencer: [],
          byCampaign: [],
        },
        error: null,
      };
    }

    const linkIds = links?.map((l) => l?.id);

    // Fetch all clicks for these links
    const { data: clicks, error: clicksError } = await supabase?.from('link_clicks')?.select('id, campaign_link_id, campaign_id, influencer_id, platform, clicked_at')?.in('campaign_link_id', linkIds);

    if (clicksError) throw clicksError;

    const allClicks = clicks || [];
    const totalClicks = allClicks?.length;

    // --- Per Platform ---
    const platformMap = {};
    allClicks?.forEach((c) => {
      const p = c?.platform || 'unknown';
      platformMap[p] = (platformMap?.[p] || 0) + 1;
    });
    const byPlatform = Object.entries(platformMap)?.map(([platform, count]) => ({ platform, count }))?.sort((a, b) => b?.count - a?.count);

    // --- Per Influencer ---
    // Build influencer_id → name map from links
    const influencerLinkMap = {};
    links?.forEach((l) => {
      if (l?.influencer_id) {
        influencerLinkMap[l.id] = l?.influencer_id;
      }
    });

    const influencerMap = {};
    allClicks?.forEach((c) => {
      const infId = c?.influencer_id || influencerLinkMap?.[c?.campaign_link_id] || null;
      if (infId) {
        influencerMap[infId] = (influencerMap?.[infId] || 0) + 1;
      }
    });
    const byInfluencer = Object.entries(influencerMap)?.map(([influencerId, count]) => ({ influencerId, count }))?.sort((a, b) => b?.count - a?.count);

    // --- Per Campaign ---
    // Build campaign_link_id → campaign title map
    const campaignLinkTitleMap = {};
    links?.forEach((l) => {
      campaignLinkTitleMap[l.id] = { title: l?.title, campaignId: l?.campaign_id };
    });

    const campaignMap = {};
    allClicks?.forEach((c) => {
      const campaignId = c?.campaign_id || campaignLinkTitleMap?.[c?.campaign_link_id]?.campaignId || c?.campaign_link_id;
      const label = campaignLinkTitleMap?.[c?.campaign_link_id]?.title || campaignId?.substring(0, 8) + '…';
      if (!campaignMap?.[campaignId]) {
        campaignMap[campaignId] = { label, count: 0 };
      }
      campaignMap[campaignId].count += 1;
    });
    const byCampaign = Object.entries(campaignMap)?.map(([id, { label, count }]) => ({ campaignId: id, label, count }))?.sort((a, b) => b?.count - a?.count);

    return {
      data: { totalClicks, byPlatform, byInfluencer, byCampaign },
      error: null,
    };
  } catch (error) {
    console.error('Error fetching link clicks analytics:', error?.message);
    return {
      data: { totalClicks: 0, byPlatform: [], byInfluencer: [], byCampaign: [] },
      error,
    };
  }
};

/**
 * Fetch campaign performance table data
 * Aggregates link_clicks per influencer with platform breakdown
 */
export const fetchCampaignPerformanceTable = async (userId) => {
  try {
    // Get all campaign links owned by this user
    const { data: links, error: linksError } = await supabase
      ?.from('campaign_links')
      ?.select('id, title, campaign_id, influencer_id')
      ?.eq('user_id', userId);

    if (linksError) throw linksError;
    if (!links || links?.length === 0) return { data: [], error: null };

    const linkIds = links?.map((l) => l?.id);

    // Fetch all clicks for these links
    const { data: clicks, error: clicksError } = await supabase
      ?.from('link_clicks')
      ?.select('id, campaign_link_id, campaign_id, influencer_id, platform, clicked_at')
      ?.in('campaign_link_id', linkIds);

    if (clicksError) throw clicksError;

    const allClicks = clicks || [];

    // Build influencer_id map from links (fallback if click row has no influencer_id)
    const linkInfluencerMap = {};
    links?.forEach((l) => {
      if (l?.influencer_id) linkInfluencerMap[l.id] = l?.influencer_id;
    });

    // Aggregate per influencer
    const influencerMap = {};

    allClicks?.forEach((c) => {
      const infId = c?.influencer_id || linkInfluencerMap?.[c?.campaign_link_id] || 'unknown';
      if (!influencerMap?.[infId]) {
        influencerMap[infId] = {
          influencerId: infId,
          influencerName: null,
          totalViews: 0,
          totalClicks: 0,
          spotify: 0,
          apple_music: 0,
          youtube_music: 0,
        };
      }
      influencerMap[infId].totalClicks += 1;
      influencerMap[infId].totalViews += 1; // each tracked event counts as a view/click

      const platform = c?.platform?.toLowerCase();
      if (platform === 'spotify') influencerMap[infId].spotify += 1;
      else if (platform === 'apple_music') influencerMap[infId].apple_music += 1;
      else if (platform === 'youtube_music') influencerMap[infId].youtube_music += 1;
    });

    // Fetch influencer names
    const influencerIds = Object.keys(influencerMap)?.filter((id) => id !== 'unknown');
    if (influencerIds?.length > 0) {
      const { data: userProfiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email')
        ?.in('id', influencerIds);

      userProfiles?.forEach((inf) => {
        if (influencerMap?.[inf?.id]) {
          influencerMap[inf?.id].influencerName =
            inf?.full_name || inf?.email?.split('@')?.[0] || `Influencer ${inf?.id?.substring(0, 6)}`;
        }
      });
    }

    // Fallback name for unknowns
    Object.keys(influencerMap)?.forEach((id) => {
      if (!influencerMap?.[id]?.influencerName) {
        influencerMap[id].influencerName = id === 'unknown' ? 'Unknown Influencer' : `Influencer ${id?.substring(0, 8)}`;
      }
    });

    const rows = Object.values(influencerMap)?.sort((a, b) => b?.totalClicks - a?.totalClicks);

    return { data: rows, error: null };
  } catch (error) {
    console.error('Error fetching campaign performance table:', error?.message);
    return { data: [], error };
  }
};

export { formatCurrency, formatNumber };